import React, { useState } from "react";
import { useLanguage } from "../contexts/LanguageContext";
import Header from "../components/Header";
import Footer from "../components/Footer";
import SEOHead from "../components/SEOHead";
import ContactModal from "../components/ContactModal";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { 
  Calendar, 
  CheckCircle, 
  ArrowRight, 
  Zap, 
  Users, 
  Target, 
  TrendingUp, 
  Shield,
  Award,
  Lightbulb,
  Settings,
  BarChart3,
  BookOpen,
  Clock,
  Play,
  CheckSquare,
  DollarSign,
  Search,
  Gavel
} from "lucide-react";

const seoConfig = {
  title: "KI-Workshops | Praxisnahe AI-Schulungen | BROVKO",
  description: "Praxisnahe KI-Workshops für Ihr Team. Von Strategie über Use-Case-Entwicklung bis zu AI-Governance. Interaktiv, maßgeschneidert und ergebnisorientiert.",
  keywords: "KI-Workshop, AI-Workshop, KI-Schulung, KI-Training, Use-Case-Workshop, AI-Governance, KI-Strategie Workshop",
  canonicalUrl: "/leistungsangebot/ki-workshops"
};

const KIWorkshops = () => {
  const { t } = useLanguage();
  const [isContactModalOpen, setIsContactModalOpen] = useState(false);

  // Modal handlers
  const openContactModal = (buttonContext = null, buttonText = null) => {
    setIsContactModalOpen(true);
  };

  const closeContactModal = () => {
    setIsContactModalOpen(false);
  };

  const workshops = [
    {
      icon: Target,
      title: "KI-Strategie-Workshop",
      subtitle: "Ihr Fahrplan zur KI-Transformation",
      features: [
        "Entwicklung einer maßgeschneiderten KI-Strategie für Ihr Unternehmen",
        "Identifikation relevanter Use Cases und Wettbewerbsvorteile",
        "Erarbeitung eines konkreten Umsetzungsplans"
      ],
      duration: "1-2 Tage"
    },
    {
      icon: Zap,
      title: "KI-Transformation-Workshop",
      subtitle: "Von der Idee zur Umsetzung",
      features: [
        "Entwicklung einer konkreten Roadmap für Ihre KI-Implementierung",
        "Auswahl der richtigen Technologien & Tools",
        "Change-Management & Mitarbeiter-Einbindung"
      ],
      duration: "1-2 Tage"
    },
    {
      icon: DollarSign,
      title: "KI-Budget-Workshop",
      subtitle: "Realistische Planung für Ihre KI-Projekte",
      features: [
        "Kostenschätzung & Budgetplanung für KI-Vorhaben",
        "Wirtschaftlichkeitsbewertung & Investitionsentscheidungen",
        "Fördermöglichkeiten & Finanzierungsmöglichkeiten"
      ],
      duration: "1 Tag"
    },
    {
      icon: BarChart3,
      title: "KI-Potentialanalyse",
      subtitle: "Wo bringt KI den größten Nutzen?",
      features: [
        "Identifikation der wertvollsten KI-Anwendungsfälle für Ihr Unternehmen",
        "Quantifizierung von Effizienzgewinnen & ROI",
        "Erarbeitung eines Umsetzungsfahrplans"
      ],
      duration: "1 Tag"
    },
    {
      icon: CheckSquare,
      title: "KI-Readiness-Assessment",
      subtitle: "Sind Sie bereit für KI?",
      features: [
        "Analyse des aktuellen Reifegrads Ihres Unternehmens für KI",
        "Identifikation von Lücken & Entwicklung einer Roadmap",
        "Einsatz eines digitalen KI-Assessment-Tools"
      ],
      duration: "0.5-1 Tag"
    },
    {
      icon: Shield,
      title: "KI-Governance-Workshop",
      subtitle: "KI sicher und rechtskonform nutzen",
      features: [
        "Einführung in den EU AI Act & regulatorische Anforderungen",
        "Erarbeitung interner Richtlinien für den KI-Einsatz",
        "Risikobewertung und Compliance-Check"
      ],
      duration: "1 Tag"
    },
    {
      icon: TrendingUp,
      title: "Sales-Intelligence-Workshop",
      subtitle: "KI für Ihren Vertrieb",
      features: [
        "KI-gestützte Lead-Generierung & Kundenanalyse",
        "Personalisierte Vertriebsstrategien mit KI",
        "Automatisierte Vertriebsprozesse & Forecasting"
      ],
      duration: "1 Tag"
    },
    {
      icon: Search,
      title: "KI-Scouting-Workshop",
      subtitle: "Die besten KI-Technologien für Ihr Unternehmen",
      features: [
        "Überblick über aktuelle KI-Technologien & Trends",
        "Auswahl relevanter Lösungen für Ihre Branche",
        "Bewertung von Anbietern & Tools"
      ],
      duration: "1 Tag"
    }
  ];

  const whyBrovko = [
    {
      icon: Clock,
      title: "Schnelle Ergebnisse",
      description: "Am Ende jedes Workshops erhalten Sie klare, umsetzbare Handlungsempfehlungen."
    },
    {
      icon: Target,
      title: "Individuelle Anpassung",
      description: "Wir gestalten die Inhalte passgenau für Ihr Unternehmen."
    },
    {
      icon: Play,
      title: "Praxisorientierung",
      description: "Alle Workshops beinhalten konkrete Anwendungsfälle, Live-Demos und interaktive Sessions."
    },
    {
      icon: CheckSquare,
      title: "KI-gestützte Checklisten",
      description: "Unsere digitalen Tools helfen Ihnen, den Workshop-Output direkt in die Praxis umzusetzen."
    }
  ];

  const methodology = [
    {
      icon: Play,
      title: "Live-Demos",
      description: "Sehen Sie KI in Aktion und testen Sie selbst."
    },
    {
      icon: BookOpen,
      title: "Use Cases",
      description: "Konkrete Beispiele aus Ihrer Branche."
    },
    {
      icon: CheckSquare,
      title: "KI-Checklisten",
      description: "Sofort nutzbare Werkzeuge für die Umsetzung."
    },
    {
      icon: Target,
      title: "Handlungsplan",
      description: "Direkt umsetzbare Maßnahmen für Ihr Unternehmen."
    }
  ];

  const workshopFormats = [
    {
      format: "Inhouse-Workshop",
      description: "Direkt bei Ihnen vor Ort mit Ihren spezifischen Herausforderungen",
      duration: "0.5 - 2 Tage",
      participants: "5-20 Teilnehmer"
    },
    {
      format: "Virtual Workshop",
      description: "Interaktive Online-Sessions mit digitalen Collaboration Tools",
      duration: "2-4 Sessions à 2-3 Stunden",
      participants: "5-15 Teilnehmer"
    },
    {
      format: "Executive Workshop",
      description: "Kompakte Führungskräfte-Sessions für strategische Entscheidungen",
      duration: "0.5 Tag",
      participants: "3-8 C-Level Executives"
    }
  ];

  const targetAudiences = [
    {
      audience: "Geschäftsführung & C-Level",
      focus: "Strategische KI-Positionierung, ROI-Bewertung, Governance",
      workshops: ["KI-Strategie", "KI-Budget", "KI-Governance"]
    },
    {
      audience: "IT-Leitung & CTO",
      focus: "Technologie-Assessment, Implementierung, Architecture",
      workshops: ["KI-Scouting", "KI-Transformation", "KI-Readiness"]
    },
    {
      audience: "Fachbereiche & Teams",
      focus: "Use Case Entwicklung, Praxisanwendung, Change Management",
      workshops: ["KI-Potentialanalyse", "Sales-Intelligence", "KI-Transformation"]
    }
  ];

  return (
    <div className="theme-brand bg-surface text-ink min-h-screen bg-gradient-to-b from-slate-900 via-slate-800 to-slate-900">
      <SEOHead {...seoConfig} />
      <Header />
      
      {/* Hero Section */}
      <section className="pt-40 pb-10">
        <div className="container mx-auto px-4">
          <div className="max-w-[46rem]">
            <h1 className="text-[clamp(2.1rem,4vw,3.4rem)] font-extrabold tracking-[-0.035em] leading-[1.05] [text-wrap:balance] text-white mb-4 leading-tight">
              {t('kiWorkshops.hero.title')}
            </h1>
            <p className="text-base text-ink-muted mb-4 leading-relaxed">
              {t('kiWorkshops.hero.description')}
            </p>
            <p className="text-sm text-ink-faint mb-6">
              {t('kiWorkshops.hero.subtitle')}
            </p>
            <Button 
              className="bg-teal-400 hover:bg-brand text-ink-onBrand px-6 py-3 text-sm font-semibold"
              onClick={() => openContactModal("Hero Section", t('kiWorkshops.hero.cta'))}
            >
              <Calendar className="mr-2 h-4 w-4" />
              {t('kiWorkshops.hero.cta')}
            </Button>
          </div>
        </div>
      </section>

      {/* Why Brovko Section */}
      <section className="py-20 md:py-28 bg-surface-raised/50">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-[clamp(1.9rem,3.6vw,3rem)] font-extrabold tracking-[-0.032em] leading-[1.06] [text-wrap:balance] text-white mb-8 text-center">
              {t('kiWorkshops.whyBrovko.title')}
            </h2>
            <div className="grid md:grid-cols-2 gap-4">
              {whyBrovko.map((item, index) => {
                const IconComponent = item.icon;
                return (
                  <div key={index} className="flex items-start gap-3">
                    <div className="bg-teal-400/10 p-2 rounded-lg flex-shrink-0">
                      <IconComponent className="h-6 w-6 text-brand-400" />
                    </div>
                    <div>
                      <h3 className="text-lg font-bold text-white mb-2">{item.title}</h3>
                      <p className="text-ink-muted text-sm leading-relaxed">{item.description}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* Workshops Section */}
      <section className="py-20 md:py-28">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-[clamp(1.9rem,3.6vw,3rem)] font-extrabold tracking-[-0.032em] leading-[1.06] [text-wrap:balance] text-white mb-8 text-center">
              {t('kiWorkshops.workshops.title')}
            </h2>
            <div className="grid md:grid-cols-2 gap-4">
              {workshops.map((workshop, index) => {
                const IconComponent = workshop.icon;
                return (
                  <div key={index} className="bg-surface-raised/50 backdrop-blur-sm rounded-xl p-5 border border-hairline hover:border-teal-400/50 transition-colors">
                    <div className="flex items-center gap-3 mb-4">
                      <div className="bg-teal-400/10 p-2 rounded-lg">
                        <IconComponent className="h-6 w-6 text-brand-400" />
                      </div>
                      <div className="flex-1">
                        <h3 className="text-lg font-bold text-white">{workshop.title}</h3>
                        <p className="text-ink-faint text-xs mt-0.5">{workshop.subtitle}</p>
                        <div className="flex items-center gap-1.5 mt-1">
                          <Clock className="h-3 w-3 text-brand-400" />
                          <span className="text-brand-400 text-xs font-medium">{workshop.duration}</span>
                        </div>
                      </div>
                    </div>
                    <ul className="space-y-2 mb-4">
                      {workshop.features.map((feature, fIndex) => (
                        <li key={fIndex} className="flex items-start gap-2">
                          <CheckCircle className="h-4 w-4 text-brand-400 mt-0.5 flex-shrink-0" />
                          <span className="text-ink-muted text-xs leading-relaxed">{feature}</span>
                        </li>
                      ))}
                    </ul>
                    <Button 
                      variant="outline" 
                      size="sm"
                      className="border-teal-400 text-brand-400 hover:bg-teal-400 hover:text-ink-onBrand w-full"
                      onClick={() => openContactModal("Workshop Services", "Planungsgespräch vereinbaren")}
                    >
                      Planungsgespräch vereinbaren
                      <ArrowRight className="ml-2 h-3 w-3" />
                    </Button>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* Workshop Formats */}
      <section className="py-20 md:py-28 bg-slate-800/30">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-[clamp(1.9rem,3.6vw,3rem)] font-extrabold tracking-[-0.032em] leading-[1.06] [text-wrap:balance] text-white mb-8 text-center">
              {t('kiWorkshops.formats.title')}
            </h2>
            <div className="grid md:grid-cols-3 gap-4">
              {workshopFormats.map((format, index) => (
                <div key={index} className="bg-surface-raised/50 rounded-lg p-5 border border-slate-700/30">
                  <h3 className="text-base font-semibold text-white mb-2">{format.format}</h3>
                  <p className="text-ink-muted text-xs mb-3 leading-relaxed">{format.description}</p>
                  <div className="space-y-1.5">
                    <div className="flex items-center gap-1.5">
                      <Clock className="h-3 w-3 text-brand-400" />
                      <span className="text-brand-400 text-xs font-medium">{format.duration}</span>
                    </div>
                    <div className="flex items-center gap-1.5">
                      <Users className="h-3 w-3 text-brand-400" />
                      <span className="text-brand-400 text-xs font-medium">{format.participants}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Methodology */}
      <section className="py-20 md:py-28">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-[clamp(1.9rem,3.6vw,3rem)] font-extrabold tracking-[-0.032em] leading-[1.06] [text-wrap:balance] text-white mb-3 text-center">
              {t('kiWorkshops.methodology.title')}
            </h2>
            <p className="text-base text-ink-faint mb-8 text-center">
              {t('kiWorkshops.methodology.subtitle')}
            </p>
            <div className="grid md:grid-cols-2 gap-4">
              {methodology.map((method, index) => {
                const IconComponent = method.icon;
                return (
                  <div key={index} className="flex items-start gap-3">
                    <div className="bg-teal-400/10 p-2 rounded-lg flex-shrink-0">
                      <IconComponent className="h-6 w-6 text-brand-400" />
                    </div>
                    <div>
                      <h3 className="text-lg font-bold text-white mb-1">{method.title}</h3>
                      <p className="text-ink-muted text-sm leading-relaxed">{method.description}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* Target Audiences */}
      <section className="py-20 md:py-28 bg-surface-raised/50">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-[clamp(1.9rem,3.6vw,3rem)] font-extrabold tracking-[-0.032em] leading-[1.06] [text-wrap:balance] text-white mb-3 text-center">
              {t('kiWorkshops.audience.title')}
            </h2>
            <p className="text-base text-ink-faint mb-6 text-center">
              {t('kiWorkshops.audience.subtitle')}
            </p>
            <div className="space-y-3">
              {targetAudiences.map((audience, index) => (
                <div key={index} className="bg-slate-800/30 rounded-lg p-4 border border-slate-700/30">
                  <div className="grid md:grid-cols-3 gap-3 items-center">
                    <h3 className="text-base font-semibold text-white">{audience.audience}</h3>
                    <p className="text-ink-muted text-xs">{audience.focus}</p>
                    <div className="flex flex-wrap gap-1.5">
                      {audience.workshops.map((workshop, wIndex) => (
                        <span key={wIndex} className="bg-teal-400/10 text-brand-400 px-2 py-0.5 rounded-full text-xs font-medium">
                          {workshop}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 md:py-28">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-[clamp(1.9rem,3.6vw,3rem)] font-extrabold tracking-[-0.032em] leading-[1.06] [text-wrap:balance] text-white mb-3">
              {t('kiWorkshops.cta.title')}
            </h2>
            <p className="text-base text-ink-muted mb-6">
              {t('kiWorkshops.cta.description')}
            </p>
            <div className="flex justify-center">
              <Button 
                className="bg-teal-400 hover:bg-brand text-ink-onBrand px-6 py-3 text-sm font-semibold"
                onClick={() => openContactModal("CTA Section", t('kiWorkshops.hero.cta'))}
              >
                <Calendar className="mr-2 h-4 w-4" />
                {t('kiWorkshops.hero.cta')}
              </Button>
            </div>
          </div>
        </div>
      </section>

      <Footer />
      
      {/* Contact Modal */}
      <ContactModal 
        isOpen={isContactModalOpen}
        onClose={closeContactModal}
        pageSource="KI-Workshops"
        buttonContext="KI-Workshop Service"
        buttonText="Unverbindliches Planungsgespräch vereinbaren"
      />
    </div>
  );
};

export default KIWorkshops;